/**
 * Command buffer operations slice for the Zustand store.
 *
 * Contains Blender-style G/R/S numeric command input: entering commands,
 * axis constraints, numeric input, confirm/cancel, and live mouse-driven
 * transform preview (translate, rotate, scale, edge slide, inset, edge loop
 * insert).
 *
 * The real work is split across sibling modules in this folder — this file
 * is just the composition layer that wires them into store actions. See
 * CLAUDE.md for the scratch-vector / command-state conventions.
 */

import { recordCommandApply } from "../../debug/listenerProbe.ts";
import type { AppState, AxisConstraint, CommandType } from "../storeTypes.ts";
import { applyCommandLive } from "./commandApplyLive.ts";
import { confirmCommand } from "./commandConfirm.ts";
import { type EnterCommandOpts, enterCommand } from "./commandEnter.ts";
import {
  adjustLastOperation,
  appendCommandDigit,
  backspaceCommand,
  cancelCommand,
  clearLastOperation,
  setCommandAxis,
} from "./commandLifecycle.ts";

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

/**
 * Command buffer store methods — call createCommandOps(set, get) inside the
 * store factory. Each method is a thin adapter that forwards to a pure
 * function in one of the sibling modules. No shared closure state lives
 * here: everything the command modules need is either in `state` (fetched
 * via `get()`) or in the module-level scratch vectors defined in `core.ts`.
 */
export function createCommandOps(set: Set_, get: Get_) {
  return {
    enterCommand: (type: CommandType, opts?: EnterCommandOpts) => {
      enterCommand(set, get, type, opts);
    },

    setCommandAxis: (axis: AxisConstraint) => {
      setCommandAxis(set, get, axis);
    },

    appendCommandDigit: (char: string) => {
      appendCommandDigit(set, get, char);
    },

    backspaceCommand: () => {
      backspaceCommand(set, get);
    },

    confirmCommand: () => {
      confirmCommand(set, get);
    },

    cancelCommand: () => {
      cancelCommand(set, get);
    },

    adjustLastOperation: (
      newParams: Record<string, number | boolean | string>,
    ) => {
      adjustLastOperation(set, get, newParams);
    },

    clearLastOperation: () => {
      clearLastOperation(set);
    },

    applyCommandLive: (
      screenX: number,
      screenY: number,
      opts?: { snapGridLevelOffset?: number },
    ) => {
      recordCommandApply(get());
      applyCommandLive(set, get, screenX, screenY, opts);
    },
  };
}
